import { useState, useCallback, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import Cast from '../cast-plugin';

interface CastState {
  isAvailable: boolean;
  isConnected: boolean;
  isConnecting: boolean;
  currentSession: chrome.cast.Session | null;
  error: string | null;
  receiverName: string | null;
  lastErrorCode: string | null;
  fallbackActive: boolean;
}

interface CastHookReturn {
  castState: CastState;
  initializeCast: () => Promise<void>;
  startCastSession: () => Promise<void>;
  stopCastSession: () => Promise<void>;
  loadMedia: (mediaInfo: {
    url: string;
    title: string;
    artist: string;
    imageUrl?: string;
  }) => Promise<void>;
  clearError: () => void;
}

export const useCast = (): CastHookReturn => {
  // Early iOS detection - if iOS, return a completely disabled Cast hook
  if (Capacitor.getPlatform() === 'ios') {
    console.log('🎬 iOS detected: Returning disabled Cast hook');
    return {
      castState: {
        isAvailable: false,
        isConnected: false,
        isConnecting: false,
        currentSession: null,
        error: null,
        receiverName: null,
        lastErrorCode: null,
        fallbackActive: false,
      },
      initializeCast: async () => {
        console.log('🎬 iOS: Cast initialization skipped');
      },
      startCastSession: async () => {
        console.log('🎬 iOS: Cast session start skipped');
      },
      stopCastSession: async () => {
        console.log('🎬 iOS: Cast session stop skipped');
      },
      loadMedia: async () => {
        console.log('🎬 iOS: Cast media loading skipped');
      },
      clearError: () => {
        console.log('🎬 iOS: Cast error clearing skipped');
      },
    };
  }

  const [castState, setCastState] = useState<CastState>({
    isAvailable: false,
    isConnected: false,
    isConnecting: false,
    currentSession: null,
    error: null,
    receiverName: null,
    lastErrorCode: null,
    fallbackActive: false,
  });

  const initializationPromiseRef = useRef<Promise<void> | null>(null);
  const isInitializedRef = useRef(false);

  const updateCastState = useCallback((updates: Partial<CastState>) => {
    // On iOS, never set Cast errors to prevent notifications
    if (Capacitor.getPlatform() === 'ios' && updates.error) {
      console.log('🎬 iOS: Suppressing Cast error:', updates.error);
      // Remove the error and ensure Cast is marked as unavailable
      updates = { 
        ...updates, 
        error: null, 
        isAvailable: false,
        isConnected: false,
        isConnecting: false 
      };
    }
    setCastState(prev => ({ ...prev, ...updates }));
  }, []);

  const clearError = useCallback(() => {
    updateCastState({ error: null, lastErrorCode: null, fallbackActive: false });
  }, [updateCastState]);

  // Step 6: Comprehensive Error Handling
  const getCastErrorDetails = useCallback((error: chrome.cast.Error | any) => {
    const errorCode = error?.code || 'UNKNOWN';
    let userMessage = 'Cast operation failed';
    let technicalMessage = error?.description || error?.message || 'Unknown error';
    let shouldRetry = false;
    let fallbackAction = null;

    // Map specific Cast error codes to user-friendly messages
    switch (errorCode) {
      case chrome.cast?.ErrorCode?.CANCEL:
        userMessage = 'Cast selection was cancelled';
        technicalMessage = 'User cancelled the Cast device selection';
        shouldRetry = false;
        break;
        
      case chrome.cast?.ErrorCode?.TIMEOUT:
        userMessage = 'No Cast devices found on your network';
        technicalMessage = 'Cast device discovery timed out';
        shouldRetry = true;
        fallbackAction = 'Try moving closer to your Cast device or check your network connection';
        break;
        
      case chrome.cast?.ErrorCode?.RECEIVER_UNAVAILABLE:
        userMessage = 'Cast device is currently unavailable';
        technicalMessage = 'Selected Cast receiver is not available';
        shouldRetry = true;
        fallbackAction = 'Make sure your Cast device is powered on and connected to the same network';
        break;
        
             case 'EXTENSION_NOT_COMPATIBLE':
       case 'EXTENSION_MISSING':
        userMessage = 'Cast extension is not available';
        technicalMessage = 'Chrome Cast extension is missing or incompatible';
        shouldRetry = false;
        fallbackAction = 'Try using Chrome browser or check your Cast extension';
        break;
        
      case chrome.cast?.ErrorCode?.API_NOT_INITIALIZED:
        userMessage = 'Cast feature is not ready';
        technicalMessage = 'Cast API not properly initialized';
        shouldRetry = true;
        fallbackAction = 'Please wait a moment and try again';
        break;
        
      case chrome.cast?.ErrorCode?.LOAD_MEDIA_FAILED:
        userMessage = 'Failed to load audio stream';
        technicalMessage = 'Media loading failed on Cast device';
        shouldRetry = true;
        fallbackAction = 'Check your internet connection and try again';
        break;
        
      case chrome.cast?.ErrorCode?.INVALID_PARAMETER:
        userMessage = 'Invalid Cast configuration';
        technicalMessage = 'Invalid parameters provided to Cast API';
        shouldRetry = false;
        fallbackAction = 'Please report this issue to support';
        break;
        
      case chrome.cast?.ErrorCode?.SESSION_ERROR:
        userMessage = 'Cast session error occurred';
        technicalMessage = 'Cast session encountered an error';
        shouldRetry = true;
        fallbackAction = 'Try disconnecting and reconnecting to your Cast device';
        break;
        
      case 'NETWORK_ERROR':
        userMessage = 'Network connection issue';
        technicalMessage = 'Network error during Cast operation';
        shouldRetry = true;
        fallbackAction = 'Check your internet connection and try again';
        break;
        
      case 'MEDIA_LOADING_TIMEOUT':
        userMessage = 'Audio stream loading timed out';
        technicalMessage = 'Media loading operation timed out';
        shouldRetry = true;
        fallbackAction = 'Check your internet connection and try again';
        break;
        
      default:
        userMessage = 'Cast operation failed';
        technicalMessage = `Unexpected Cast error: ${technicalMessage}`;
        shouldRetry = true;
        fallbackAction = 'Try again or restart the app';
        break;
    }

    return {
      code: errorCode,
      userMessage,
      technicalMessage,
      shouldRetry,
      fallbackAction,
      originalError: error
    };
  }, []);

  const handleCastError = useCallback((error: chrome.cast.Error | any, context: string) => {
    // Skip error handling entirely on iOS
    if (Capacitor.getPlatform() === 'ios') {
      console.log('🎬 iOS: Suppressing Cast error handling');
      return { userMessage: '', code: '', shouldRetry: false };
    }
    
    const errorDetails = getCastErrorDetails(error);
    
    console.error(`🎬 Cast error in ${context}:`, {
      code: errorDetails.code,
      userMessage: errorDetails.userMessage,
      technicalMessage: errorDetails.technicalMessage,
      shouldRetry: errorDetails.shouldRetry,
      fallbackAction: errorDetails.fallbackAction,
      originalError: errorDetails.originalError
    });

    // Update state with comprehensive error information
    updateCastState({
      error: errorDetails.userMessage,
      lastErrorCode: errorDetails.code,
      fallbackActive: !!errorDetails.fallbackAction,
      isConnecting: false,
      isConnected: false
    });

    // Activate fallback behavior if available
    if (errorDetails.fallbackAction && errorDetails.shouldRetry) {
      setTimeout(() => {
        activateFallbackBehavior(errorDetails);
      }, 1000); // Small delay before activating fallback
    }

    // Return error details for further handling
    return errorDetails;
  }, [getCastErrorDetails, updateCastState]);



  const waitForCastAPI = useCallback((timeout = 10000): Promise<void> => {
    return new Promise((resolve, reject) => {
      // Skip entirely on iOS
      if (Capacitor.getPlatform() === 'ios') {
        console.log('🎬 iOS: Skipping Cast API wait');
        resolve(); // Always resolve on iOS
        return;
      }
      
      const startTime = Date.now();
      
      const checkAPI = () => {
        if (window.chrome?.cast?.isAvailable) {
          console.log('🎬 Cast API confirmed available');
          resolve();
          return;
        }
        
        if (Date.now() - startTime > timeout) {
          reject(new Error('Cast timeout'));
          return;
        }
        
        setTimeout(checkAPI, 100);
      };
      
      checkAPI();
    });
  }, []);

  const loadCastSDK = useCallback((): Promise<void> => {
    return new Promise((resolve, reject) => {
      // Skip Cast SDK loading on iOS - always resolve
      if (Capacitor.getPlatform() === 'ios') {
        console.log('🎬 iOS detected: Skipping Cast SDK loading');
        resolve(); // Always resolve on iOS to prevent any errors
        return;
      }
      
      // Check if Cast SDK is already loaded and available
      if (window.chrome?.cast?.isAvailable) {
        console.log('🎬 Cast SDK already available');
        resolve();
        return;
      }
      
      // Check if script is already present
      const existingScript = document.querySelector('script[src*="cast_sender.js"]');
      if (existingScript) {
        console.log('🎬 Cast SDK script already present, waiting for API...');
        waitForCastAPI().then(resolve).catch(reject);
        return;
      }
      
      console.log('🎬 Loading Cast SDK...');
      const script = document.createElement('script');
      script.src = 'https://www.gstatic.com/cv/js/sender/v1/cast_sender.js?loadCastFramework=1';
      
      // Set up callback for when SDK v3 is ready
      window['__onGCastApiAvailable'] = (isAvailable: boolean) => {
        // Skip callback entirely on iOS - always resolve
        if (Capacitor.getPlatform() === 'ios') {
          console.log('🎬 iOS: Cast SDK v3 callback blocked');
          resolve(); // Always resolve on iOS to prevent any errors
          return;
        }
        
        console.log('🎬 Cast SDK v3 callback:', isAvailable);
        if (isAvailable && window.cast?.framework) {
          resolve();
        } else {
          setTimeout(() => {
            if (window.cast?.framework) {
              resolve();
            } else {
              reject(new Error('Cast v3 timeout'));
            }
          }, 500);
        }
      };
      
      script.onload = () => {
        console.log('🎬 Cast SDK script loaded');
        waitForCastAPI(8000).then(resolve).catch(reject);
      };
      
      script.onerror = () => {
        reject(new Error('Failed to load Cast SDK script'));
      };
      
      document.head.appendChild(script);
      
      // Overall timeout
              setTimeout(() => {
          if (!window.chrome?.cast?.isAvailable) {
            reject(new Error('Cast timeout'));
          }
        }, 15000);
    });
  }, [waitForCastAPI]);

  const initializeCast = useCallback(async (): Promise<void> => {
    // Skip Cast initialization entirely on iOS - only AirPlay is supported
    if (Capacitor.getPlatform() === 'ios') {
      console.log('🎬 iOS detected: Skipping Cast initialization (AirPlay-only mode)');
      
      // Clean up any existing Cast SDK script or callback that might be present
      const existingScript = document.querySelector('script[src*="cast_sender.js"]');
      if (existingScript) {
        console.log('🎬 iOS: Removing existing Cast SDK script');
        existingScript.remove();
      }
      
      // Clear any existing Cast API callback
      if (window['__onGCastApiAvailable']) {
        console.log('🎬 iOS: Clearing Cast API callback');
        window['__onGCastApiAvailable'] = undefined;
      }
      
      // Disable Cast API entirely on iOS by overriding chrome.cast
      if (window.chrome) {
        console.log('🎬 iOS: Disabling chrome.cast API');
        window.chrome.cast = {
          isAvailable: false,
          initialize: () => {},
          requestSession: () => {},
          ErrorCode: {}
        };
      }
      
      updateCastState({ 
        isAvailable: false, 
        error: null,
        isConnected: false,
        isConnecting: false 
      });
      isInitializedRef.current = true; // Mark as initialized to prevent retries
      return; // Exit early on iOS without any errors
    }

    // Prevent multiple simultaneous initializations
    if (initializationPromiseRef.current) {
      return initializationPromiseRef.current;
    }

    if (isInitializedRef.current) {
      console.log('🎬 Cast already initialized');
      return;
    }

    initializationPromiseRef.current = (async () => {
      try {
        console.log('🎬 Starting Cast initialization...');
        updateCastState({ error: null });

        // For Android, use native plugin
        if (Capacitor.getPlatform() === 'android') {
          const result = await Cast.isAvailable();
          if (result.available) {
                                      Cast.addListener('sessionStarted', () => {
               updateCastState({ 
                 isConnected: true, 
                 isConnecting: false,
                 receiverName: 'Chromecast'
               });
             });
             Cast.addListener('sessionEnded', () => {
               updateCastState({ 
                 isConnected: false, 
                 isConnecting: false,
                 currentSession: null,
                 receiverName: null
               });
             });
            updateCastState({ isAvailable: true });
            isInitializedRef.current = true;
            console.log('🎬 Android Cast initialized');
          } else {
            throw new Error('Cast not available on Android');
          }
          return;
        }

        // For web platforms
        await loadCastSDK();
        
        if (!window.chrome?.cast?.isAvailable) {
          throw new Error('Cast not available');
        }

        const sessionRequest = new chrome.cast.SessionRequest(
          chrome.cast.media.DEFAULT_MEDIA_RECEIVER_APP_ID
        );
        
        const apiConfig = new chrome.cast.ApiConfig(
          sessionRequest,
          (session: chrome.cast.Session) => {
            console.log('🎬 Cast session established:', session.receiver.friendlyName);
            updateCastState({
              isConnected: true,
              isConnecting: false,
              currentSession: session,
              receiverName: session.receiver.friendlyName,
              error: null
            });
            
            session.addUpdateListener((isAlive: boolean) => {
              if (!isAlive) {
                console.log('🎬 Cast session ended');
                updateCastState({
                  isConnected: false,
                  currentSession: null,
                  receiverName: null
                });
              }
            });
          },
          (availability: chrome.cast.ReceiverAvailability) => {
            console.log('🎬 Cast receiver availability:', availability);
            updateCastState({ 
              isAvailable: availability === chrome.cast.ReceiverAvailability.AVAILABLE
            });
          }
        );

        chrome.cast.initialize(apiConfig, () => {
          console.log('🎬 Cast initialized successfully');
          updateCastState({ isAvailable: true, error: null });
          isInitializedRef.current = true;
        }, (error: chrome.cast.Error) => {
          const errorDetails = handleCastError(error, 'initialization');
          console.error('🎬 Cast initialization failed:', errorDetails.userMessage);
          updateCastState({ 
            isAvailable: false, 
            error: errorDetails.userMessage 
          });
        });

      } catch (error) {
        console.error('🎬 Cast initialization error:', error);
        // Don't set error state on iOS
        if (Capacitor.getPlatform() !== 'ios') {
          updateCastState({ 
            isAvailable: false, 
            error: error instanceof Error ? error.message : 'Cast initialization failed' 
          });
        }
        isInitializedRef.current = false;
      } finally {
        initializationPromiseRef.current = null;
      }
    })();

    return initializationPromiseRef.current;
  }, [loadCastSDK, updateCastState]);

  // Step 6: Fallback behavior function (added after initializeCast is defined)
  const activateFallbackBehavior = useCallback(async (errorDetails: any) => {
    console.log('🎬 Activating fallback behavior:', errorDetails.fallbackAction);
    
    // Implement specific fallback behaviors
    switch (errorDetails.code) {
      case chrome.cast?.ErrorCode?.TIMEOUT:
      case chrome.cast?.ErrorCode?.RECEIVER_UNAVAILABLE:
        // Auto-retry after a delay for network-related issues
        setTimeout(() => {
          console.log('🎬 Auto-retrying Cast operation after network error...');
          updateCastState({ fallbackActive: false });
        }, 5000);
        break;
        
      case chrome.cast?.ErrorCode?.API_NOT_INITIALIZED:
        // Try to reinitialize Cast API
        console.log('🎬 Attempting to reinitialize Cast API...');
        try {
          await initializeCast();
          updateCastState({ fallbackActive: false });
        } catch (reinitError) {
          console.error('🎬 Cast API reinitialization failed:', reinitError);
        }
        break;
        
      default:
        // Generic fallback - clear error after delay
        setTimeout(() => {
          updateCastState({ fallbackActive: false });
        }, 10000);
        break;
    }
  }, [updateCastState, initializeCast]);

  const startCastSession = useCallback(async (): Promise<void> => {
    // iOS: Cast is not supported, should not reach here
    if (Capacitor.getPlatform() === 'ios') {
      console.log('🎬 iOS: Cast not supported, use AirPlay instead');
      // Don't throw an error on iOS - just return silently
      return;
    }
    
    try {

      updateCastState({ isConnecting: true, error: null });

      // Android native
      if (Capacitor.getPlatform() === 'android') {
        await Cast.requestSession();
        return;
      }

      // Web
      if (!window.chrome?.cast?.requestSession) {
        throw new Error('Cast not available');
      }

      return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          updateCastState({ isConnecting: false });
          reject(new Error('Cast session timeout'));
        }, 30000);

        chrome.cast.requestSession(
          (session: chrome.cast.Session) => {
            clearTimeout(timeout);
            console.log('🎬 Cast session created:', session.receiver.friendlyName);
            updateCastState({
              isConnected: true,
              isConnecting: false,
              currentSession: session,
              receiverName: session.receiver.friendlyName,
              error: null
            });
            resolve();
          },
          (error: chrome.cast.Error) => {
            clearTimeout(timeout);
            
            const errorDetails = handleCastError(error, 'session creation');
            console.error('🎬 Cast session failed:', errorDetails.userMessage);
            
                         updateCastState({ 
               isConnecting: false,
               error: errorDetails.userMessage,
               lastErrorCode: errorDetails.code,
               fallbackActive: false
             });
            
            reject(new Error(errorDetails.userMessage));
          }
        );
      });
    } catch (error) {
      // Don't set error state on iOS
      if (Capacitor.getPlatform() !== 'ios') {
        updateCastState({ 
          isConnecting: false, 
          error: error instanceof Error ? error.message : 'Failed to start Cast session' 
        });
      }
      throw error;
    }
  }, [updateCastState]);

  const stopCastSession = useCallback(async (): Promise<void> => {
    try {
      // Android native
      if (Capacitor.getPlatform() === 'android') {
        await Cast.stopCasting();
        return;
      }

      // Web
      if (castState.currentSession) {
        return new Promise((resolve, reject) => {
          castState.currentSession!.stop(
            () => {
              console.log('🎬 Cast session stopped');
              updateCastState({
                isConnected: false,
                currentSession: null,
                receiverName: null
              });
              resolve();
            },
            (error: chrome.cast.Error) => {
              const errorDetails = handleCastError(error, 'session termination');
              reject(new Error(errorDetails.userMessage));
            }
          );
        });
      }
    } catch (error) {
      updateCastState({ 
        error: error instanceof Error ? error.message : 'Failed to stop Cast session' 
      });
      throw error;
    }
  }, [castState.currentSession, updateCastState]);

  // Step 5: Enhanced media loading with CORS handling and retry logic
  const validateStreamUrl = useCallback(async (url: string): Promise<boolean> => {
    try {
      console.log('🎬 Validating stream URL accessibility:', url);
      const response = await fetch(url, { 
        method: 'HEAD',
        mode: 'cors',
        cache: 'no-cache'
      });
      
      if (response.ok) {
        console.log('🎬 Stream URL is accessible');
        return true;
      } else {
        console.warn('🎬 Stream URL returned non-OK status:', response.status);
        return false;
      }
    } catch (error) {
      console.warn('🎬 Stream URL validation failed:', error);
      // Don't fail completely - some CORS restrictions might prevent HEAD requests
      // but the stream might still work on Chromecast
      return true;
    }
  }, []);



  const loadMedia = useCallback(async (mediaInfo: {
    url: string;
    title: string;
    artist: string;
    imageUrl?: string;
  }): Promise<void> => {
    try {
      console.log('🎬 Starting media loading process:', mediaInfo);
      
      // Step 5.1: Validate stream URL accessibility
      const isUrlAccessible = await validateStreamUrl(mediaInfo.url);
      if (!isUrlAccessible) {
        console.warn('🎬 Stream URL may not be accessible, but continuing...');
      }

      // Android native
      if (Capacitor.getPlatform() === 'android') {
        console.log('🎬 Loading media via Android native plugin');
        await Cast.loadMedia({
          url: mediaInfo.url,
          title: mediaInfo.title,
          artist: mediaInfo.artist,
          imageUrl: mediaInfo.imageUrl || '/logo.png'
        });
        return;
      }

      // Web - Step 5.2: Enhanced media info with proper CORS headers
      if (!castState.currentSession) {
        throw new Error('No active Cast session');
      }

      // Create media info with enhanced configuration
      const castMediaInfo = new chrome.cast.media.MediaInfo(mediaInfo.url, 'audio/mpeg');
      
      // Step 5.3: Add proper metadata
      castMediaInfo.metadata = new chrome.cast.media.MusicTrackMediaMetadata();
      castMediaInfo.metadata.title = mediaInfo.title;
      castMediaInfo.metadata.artist = mediaInfo.artist;
      castMediaInfo.metadata.albumName = '24/7 From Ireland With Love';
      
      // Add artwork with fallback
      const artworkUrl = mediaInfo.imageUrl || `${window.location.origin}/logo.png`;
      castMediaInfo.metadata.images = [new chrome.cast.Image(artworkUrl)];
      
      // Configure for live streaming
      castMediaInfo.streamType = chrome.cast.media.StreamType.LIVE;

      const request = new chrome.cast.media.LoadRequest(castMediaInfo);
      request.autoplay = true;
      request.currentTime = 0;

      return new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('Media loading timeout'));
        }, 15000);

        castState.currentSession!.loadMedia(
          request,
          () => {
            clearTimeout(timeout);
            console.log('🎬 Media loaded to Cast device successfully');
            resolve();
          },
          (error: chrome.cast.Error) => {
            clearTimeout(timeout);
            const errorDetails = handleCastError(error, 'media loading');
            console.error('🎬 Media loading failed:', errorDetails.userMessage);
            reject(new Error(errorDetails.userMessage));
          }
        );
      });
      
      console.log('🎬 Media loading completed successfully');
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load media';
      console.error('🎬 Final media loading error:', errorMessage);
      updateCastState({ error: errorMessage });
      throw error;
    }
  }, [castState.currentSession, updateCastState, validateStreamUrl]);

  return {
    castState,
    initializeCast,
    startCastSession,
    stopCastSession,
    loadMedia,
    clearError,
  };
};